import React, { useState, useEffect } from 'react';

interface CreatePieceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PieceFormData) => void;
  batiments: Array<{ id: string; code: string; nom: string }>;
  editingPiece?: any;
}

export interface PieceFormData {
  nom: string;
  code: string;
  numero?: string;
  etage?: number;
  batimentId: string;
}

const CreatePieceModal: React.FC<CreatePieceModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  batiments,
  editingPiece
}) => {
  const [formData, setFormData] = useState<PieceFormData>({
    nom: '',
    code: '',
    numero: '',
    etage: 0,
    batimentId: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Pré-remplir le formulaire en mode édition
  useEffect(() => {
    if (editingPiece) {
      setFormData({
        nom: editingPiece.nom || '',
        code: editingPiece.code || '',
        numero: editingPiece.numero || '',
        etage: editingPiece.etage || 0,
        batimentId: editingPiece.batimentId || '',
      });
    } else {
      setFormData({
        nom: '',
        code: '',
        numero: '',
        etage: 0,
        batimentId: '',
      });
    }
  }, [editingPiece, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'number' ? parseInt(value) || 0 : value 
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.nom.trim()) newErrors.nom = 'Le nom est obligatoire';
    if (!formData.code.trim()) newErrors.code = 'Le code est obligatoire';
    if (!formData.batimentId) newErrors.batimentId = 'Le bâtiment est obligatoire';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
      setFormData({ nom: '', code: '', numero: '', etage: 0, batimentId: '' });
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  🚪 {editingPiece ? 'Modifier la Pièce' : 'Nouvelle Pièce'}
                </h3>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              <div className="space-y-4">
                {/* Bâtiment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bâtiment <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="batimentId"
                    value={formData.batimentId}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.batimentId ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Sélectionner un bâtiment</option>
                    {batiments.map(batiment => (
                      <option key={batiment.id} value={batiment.id}>
                        {batiment.nom} ({batiment.code})
                      </option>
                    ))}
                  </select>
                  {errors.batimentId && <p className="mt-1 text-sm text-red-600">{errors.batimentId}</p>}
                </div>

                {/* Nom */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de la pièce <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.nom ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Salle pédagogique, Cuisine, Dortoir 1"
                  />
                  {errors.nom && <p className="mt-1 text-sm text-red-600">{errors.nom}</p>}
                </div>

                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.code ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: ROB2-SALLE-PEDAGOGIQUE"
                    maxLength={50}
                  />
                  {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
                  <p className="mt-1 text-xs text-gray-500">Format: [BATIMENT]-[NOM]</p>
                </div>

                {/* Numéro */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro
                  </label>
                  <input
                    type="text"
                    name="numero"
                    value={formData.numero}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 101, A12"
                  />
                </div>

                {/* Étage */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Étage
                  </label>
                  <input
                    type="number"
                    name="etage"
                    value={formData.etage}
                    onChange={handleChange}
                    min="0"
                    max="10"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">0 = RDC, 1 = 1er étage, etc.</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {editingPiece ? 'Modifier la pièce' : 'Créer la pièce'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreatePieceModal;

